/**
 * Fetch Path Preview State API
 * POST /api/learning-paths/{pathId}/preview-state
 *
 * Admin-only preview: returns the SAME viewer-state v2 payload the learner
 * viewer renders (GET /viewer-state), but computed by the ProgressionEngine
 * over a transient (never-persisted) enrollment and a caller-supplied
 * simulated-completion map. This is the consistency guarantee — preview and
 * the published viewer share one engine and one payload shape, so ordering,
 * locking, requireAll, time gates and decision/quiz branching are identical.
 *
 * POST (not GET) so the simulation body rides along; an empty completions map
 * yields the fresh, nothing-completed state.
 */

import axiosInstance from "../axiosInstance";
import type {
  PreviewCompletionsMap,
  PreviewPersonalization,
  ViewerState,
} from "./types";

interface ViewerStateEnvelope {
  data: ViewerState;
}

export const fetchPathPreviewState = async (
  pathId: number,
  completions: PreviewCompletionsMap = {},
  personalization?: PreviewPersonalization | null,
  simulatedDaysElapsed?: number | null,
): Promise<ViewerState> => {
  const response = await axiosInstance.post<ViewerStateEnvelope>(
    `/api/learning-paths/${pathId}/preview-state`,
    {
      completions,
      personalization: personalization ?? null,
      simulatedDaysElapsed: simulatedDaysElapsed ?? null,
    },
  );

  return response.data.data;
};
